import { DraggableTable, getGridColumns, Modal, Select, SelectOption } from '@/components/common';
import { ScrollType } from '@/Helper';
import { BaseColumns, GridColumn } from '@/model/CommonModel';
import { StatusGroupNotificationStatus, StatusObject, StatusObjectEnum } from '@/model/StatusViewerModel';
import { useStoreDispatch, useStoreSelector } from '@/store';
import { RootReducer } from '@/store/rootReducer';
import {
	loadStatusViewerColumnConfigurationAction,
	saveStatusViewerColumnConfiguration,
	updateStatusViewerColumnConfigurationState,
} from '@/store/statusViewer/actions';
import { selectSelectedStatusGroup } from '@/store/statusViewer/selectors';
import { arrayMove } from '@dnd-kit/sortable';
import { Button, notification, Table } from 'antd';
import { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import styles from './statusViewerModalColumns.module.scss';

//Avoid creating object style inline, since increases reconciliations
const scroll: ScrollType = { y: 465 };
const statusViewerObjectSelectId = 'statusViewerObjectSelect';
const statusViewerColumnsTableId = 'statusViewerColumnsTable';

type DataType = { index: number; Header: string } & GridColumn & BaseColumns;

type Props = {
	onSetVisible: () => void;
	statusGroupFilter: SelectOption;
};

type DataSourceLocal = {
	KeyObject: string;
	DataSource: GridColumn[];
};

const StatusViewerModalColumns: React.FC<Props> = ({ onSetVisible, statusGroupFilter }) => {
	//#region State Management Variables
	const dispatch = useStoreDispatch();
	const [hasError, setHasError] = useState(false);
	const [dataSourceTable, setDataSourceTable] = useState<DataType[]>([]);
	const [optionShowOnlySelection, setOptionShowOnlySelection] = useState('-1');
	const [rowKeysSelected, setRowKeysSelected] = useState<string[]>([]);
	const [statusViewerDataSourceLocal, setStatusViewerDataSource] = useState<DataSourceLocal[]>([]);
	const statusViewerColumnConfigurationStore: Record<StatusObjectEnum, GridColumn[]> = useStoreSelector(
		(state: RootReducer): Record<StatusObjectEnum, GridColumn[]> => state.statusViewerReducer.statusViewerColumnConfiguration
	);
	const selectedStatusGroup = useStoreSelector(selectSelectedStatusGroup);
	//#endregion

	useEffect(() => {
		dispatch(loadStatusViewerColumnConfigurationAction());
	}, []);

	useEffect(() => {
		if (statusViewerColumnConfigurationStore) {
			const statusViewerColumnConfigurationLocal: DataSourceLocal[] = Object.keys(statusViewerColumnConfigurationStore).map(m => {
				return {
					KeyObject: m,
					DataSource: [...statusViewerColumnConfigurationStore[m]],
				};
			});
			setStatusViewerDataSource(statusViewerColumnConfigurationLocal);
			if (optionShowOnlySelection !== '-1') {
				updateDataSourceTableAndSelectedRowKeys(optionShowOnlySelection, statusViewerColumnConfigurationLocal);
			}
		}
	}, [statusViewerColumnConfigurationStore]);

	const updateDataSourceTableAndSelectedRowKeys = (optionShowOnlySelection: string, statusViewerColumnConfiguration: DataSourceLocal[]) => {
		const dataSource = statusViewerColumnConfiguration.find(f => f.KeyObject === StatusObject[Number(optionShowOnlySelection)]).DataSource;
		const dataTable = dataSource
			.filter(x => !x.IsAuxColumn)
			.map((m, index) => ({
				...m,
				index: dataSource.indexOf(m),
				Header: _(m.Header),
				key: index.toString(),
			}));
		setDataSourceTable(dataTable);
		setRowKeysSelected(dataTable.filter(f => f.Visible === true).map(m => m.index.toString()));
	};

	const onSelectChangeCheckBox = (selectedRowKeys: string[]) => {
		let updateData: Record<StatusObjectEnum, GridColumn[]> = { ...statusViewerColumnConfigurationStore };
		const updateItem: GridColumn[] = dataSourceTable.map<GridColumn>(m => {
			const found = selectedRowKeys.find(f => f === m.index.toString());
			return {
				...m,
				Visible: found !== undefined ? true : false,
			};
		});
		updateData[StatusObject[Number(optionShowOnlySelection)]] = updateItem;
		setHasError(selectedRowKeys.length > 0 ? false : true);
		setRowKeysSelected(selectedRowKeys);
		dispatch(updateStatusViewerColumnConfigurationState(updateData));
	};

	const rowSelection: TableRowSelection<DataType> = {
		selectedRowKeys: rowKeysSelected,
		onChange: onSelectChangeCheckBox,
		selections: optionShowOnlySelection === '-1' ? false : [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
		getCheckboxProps: record => ({ id: `statusViewerConfigureColumns-${record.Header}` }),
	};

	const columns: ColumnsType<DataType> = [
		{
			title: _('Column'),
			dataIndex: 'Header',
		},
		{
			key: 'sort',
			title: _('Order'),
			dataIndex: 'sort',
			width: 55,
		},
	];

	const handleOnSave = async () => {
		const notificationStatusConfig: StatusGroupNotificationStatus = {
			statusGroupSelect: selectedStatusGroup,
			onSetVisible: onSetVisible,
			permissionErrorCallback: errorMessage => {
				notification['error']({
					message: errorMessage,
				});
			},
			onHandleCloseModal: handleCloseModal,
		};

		const columnsToSave: Record<StatusObjectEnum, GridColumn[]> = { ...statusViewerColumnConfigurationStore };
		for (const [key, value] of Object.entries(statusViewerColumnConfigurationStore)) {
			columnsToSave[key] = getGridColumns(value);
		}

		dispatch(saveStatusViewerColumnConfiguration(columnsToSave, notificationStatusConfig));
	};

	const onHandleFilterChange = (value: string) => {
		setOptionShowOnlySelection(value);
		updateDataSourceTableAndSelectedRowKeys(value, statusViewerDataSourceLocal);
	};

	const handleCloseModal = () => {
		onSetVisible();
	};
	const onSortEnd = ({ oldIndex, newIndex }) => {
		if (oldIndex !== newIndex) {
			const newData = arrayMove([].concat(dataSourceTable), oldIndex, newIndex).filter(el => !!el);
			let updateData: Record<StatusObjectEnum, GridColumn[]> = { ...statusViewerColumnConfigurationStore };
			const updateItem: GridColumn[] = newData.map<GridColumn>(m => {
				return {
					...m,
					Order: newData.indexOf(m) + 1,
				};
			});
			updateData[StatusObject[Number(optionShowOnlySelection)]] = updateItem;
			setDataSourceTable(newData);
			dispatch(updateStatusViewerColumnConfigurationState(updateData));
		}
	};

	const statusGroupObjects: SelectOption = statusGroupFilter.map(x => ({ ...x, id: `${x.id}-ColumnConfigurationOption` }));

	return (
		<Modal
			footer={[
				<Button id='statusViewerColumnsSaveButton' key='save' type='primary' disabled={hasError} onClick={() => handleOnSave()}>
					{_('SaveChanges')}
				</Button>,
				<Button id='statusViewerColumnsCancelButton' key='cancel' onClick={() => handleCloseModal()}>
					{_('Cancel')}
				</Button>,
			]}
			width='400px'
			visible={true}
			title={_('ConfigureColumnHeaders')}
			onCancel={() => handleCloseModal()}
			onClickOk={() => null}>
			<div id='statusViewerColumnsContainer' className={styles.container}>
				<div>
					<label htmlFor={statusViewerObjectSelectId}>{_('Object')}</label>
					<Select
						id={statusViewerObjectSelectId}
						options={statusGroupObjects}
						defaultValue={optionShowOnlySelection}
						onChange={onHandleFilterChange}
						className={styles.selectOptionStatusViewer}
						listHeight={320}
						disabled={hasError}
					/>
				</div>
				<div>{_('ConfigureColumnInstructions')}</div>
				<div className={cx({ [styles.error]: hasError })}>
					<DraggableTable<DataType>
						id={statusViewerColumnsTableId}
						scroll={scroll}
						pagination={false}
						dataSource={dataSourceTable}
						columns={columns}
						rowSelection={rowSelection}
						onSortEnd={onSortEnd}
						className={styles.tableColumnsOptions}
					/>
				</div>
				{hasError && (
					<label htmlFor={statusViewerColumnsTableId} className={styles.error}>
						{_('AtLeastOneItem')}
					</label>
				)}
			</div>
		</Modal>
	);
};

export { StatusViewerModalColumns };
